import React, { useRef, useState, useEffect } from "react";
import { Icons } from "../../../../views/crm-pages/conversation/constant";
import { EmailEditorCore, EmailEditorCoreRef } from "../../../shared/email-editor-core";

interface EmailEditorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (html: string, design: object) => void;
  initialDesign?: object;
}

export const EmailEditorDialog: React.FC<EmailEditorDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  initialDesign,
}) => {
  const editorRef = useRef<EmailEditorCoreRef | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditorReady, setIsEditorReady] = useState(false);

  // Reset editor ready state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setIsEditorReady(false);
    }
  }, [isOpen]);

  // Load design when editor is ready
  const handleEditorReady = () => {
    setIsEditorReady(true);
    // Load initial design when editor is ready
    if (initialDesign && editorRef.current) {
      editorRef.current.loadDesign(initialDesign);
    }
  };

  const exportHtml = () => {
    setIsSaving(true);
    editorRef.current?.exportHtml((data) => {
    
      if (onSave) {
        onSave(data.html, data.design);
      }

      setTimeout(() => {
        setIsSaving(false);
        onClose();
      }, 500);
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1000]">
      <div className="w-[95vw] h-[95vh] bg-white dark:bg-gray-900 rounded-xl flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-5 py-3 bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#9ED8B5] dark:bg-emerald-500/20 flex items-center justify-center">
              <Icons.Email size={16} />
            </div>
            <div>
              <h2 className="m-0 text-base font-bold text-slate-800 dark:text-gray-100">
                Email Template Editor
              </h2>
              <p className="m-0 text-xs text-slate-500 dark:text-gray-400">
                Drag and drop elements to build your email
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 dark:text-gray-500 border-none rounded-lg cursor-pointer text-xl hover:bg-slate-100 dark:hover:bg-gray-800 hover:text-slate-600 dark:hover:text-gray-300 transition-colors bg-transparent"
          >
            ×
          </button>
        </div>

        {/* Editor Container */}
        <div className="flex-1 relative bg-[#f7f8fb] dark:bg-gray-950">
          <EmailEditorCore ref={editorRef} minHeight="100%" onReady={handleEditorReady} />
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center px-5 py-3 border-t border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900 gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm bg-white dark:bg-gray-800 text-slate-600 dark:text-gray-300 border border-slate-200 dark:border-gray-700 rounded-lg cursor-pointer font-semibold hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={exportHtml}
            disabled={isSaving || !isEditorReady}
            className="px-5 py-2 text-sm bg-[#1C6E46] text-white border-none rounded-lg cursor-pointer font-semibold hover:bg-[#155a39] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSaving ? "Saving..." : "Save Template"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailEditorDialog;
